import { useLayoutEffect, useRef } from 'react';

import type { Round } from '@h2h/shared';
import { Lock } from '../ui/icons';
import { cn } from '../../lib/cn';
import { stagePhaseTone } from '../../screens/play/stages-tab/helpers';

export type RoundDateStripItem = {
  roundId: string;
  kickoffAt: number;
  status?: Round['status'];
};

function dateParts(ms: number) {
  const d = new Date(ms);
  return {
    top: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    day: String(d.getDate()),
    month: d.toLocaleDateString('en-GB', { month: 'short' })
  };
}

/**
 * Horizontal date strip for browsing rounds/stages — day + month, not cryptic S1/S2 chips.
 */
export function RoundDateStrip({
  items,
  selectedId,
  currentRoundId,
  onSelect
}: {
  items: RoundDateStripItem[];
  selectedId: string | null;
  currentRoundId?: string | null;
  onSelect: (roundId: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [selectedId]);

  return (
    <div
      ref={scrollRef}
      className="flex gap-2 overflow-x-auto px-[18px] pb-3.5 pt-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {items.map((item) => {
        const active = item.roundId === selectedId;
        const locked = item.status === 'SCHEDULED';
        const live =
          stagePhaseTone(item.status) === 'live' || stagePhaseTone(item.status) === 'settling';
        const isCurrent = !!currentRoundId && item.roundId === currentRoundId;
        const parts = dateParts(item.kickoffAt);

        return (
          <button
            key={item.roundId}
            type="button"
            data-active={active ? 'true' : undefined}
            onClick={() => onSelect(item.roundId)}
            className={cn(
              'tap relative flex w-[58px] shrink-0 flex-col items-center rounded-[15px] px-2 py-2 transition-colors',
              active && 'bg-night text-white shadow-[0_6px_18px_-8px_rgba(0,0,0,0.55)]',
              !active && locked && 'bg-[#F6F6F9] text-[#B4B4BC]',
              !active && !locked && 'bg-fill text-ink2',
              isCurrent && !active && 'ring-2 ring-accent/45 ring-offset-2 ring-offset-white'
            )}
          >
            {live && !active && (
              <span className="absolute right-1.5 top-1.5 h-[6px] w-[6px] rounded-full bg-[#E3A82B]" />
            )}

            {locked ? (
              <>
                <Lock
                  width={14}
                  height={14}
                  strokeWidth={2.2}
                  className={active ? 'text-white/80' : 'text-[#B4B4BC]'}
                />
                <span className="mt-1 text-[9px] font-bold uppercase tracking-[0.06em] opacity-70">
                  {parts.month}
                </span>
              </>
            ) : (
              <>
                <span
                  className={cn(
                    'w-full truncate text-center text-[10px] font-bold uppercase tracking-[0.04em]',
                    active ? 'text-white/75' : 'text-ink3'
                  )}
                >
                  {parts.top}
                </span>
                <span className="num mt-0.5 text-[18px] font-extrabold leading-none tracking-[-0.03em]">
                  {parts.day}
                </span>
                <span className={cn('mt-0.5 text-[10px] font-semibold', active ? 'text-white/70' : 'text-ink3')}>
                  {parts.month}
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
